import type { THomeCountSource, THomeTile, THomeTileSpec } from '@/constants/home';
import type { TCounterBucket, TCounters } from '@/services/counters';
import type { TInterviewStatus, TStaffInterview } from '@/services/interviews';
import type { TCheckCountFacet } from '@/hooks/verification';
import type { TMemberCountFacet } from '@/hooks/members';

import { MY_APPLICANTS_OPEN_STATUSES } from '@/constants/applicants';
import { sumKeys } from '@/utils/navBadges';

type THomeSources = {
  counters?: TCounters;
  interviews?: readonly TStaffInterview[];
  openChecks?: number;
  allClients?: number;
  allSingles?: number;
  unreadMessages: number;
};

const OWN_STATUS: TCounterBucket = 'own_status';

const PIPELINE_STATUS: TCounterBucket = 'pipeline_status';

const BOOK_ROLE: TCounterBucket = 'book_role';

const REVIEW_OPEN: TCounterBucket = 'review_open';

const OPEN_INTERVIEW_STATUSES: readonly TInterviewStatus[] = ['upcoming', 'assessment_due'];

const HOME_CHECK_FACETS: readonly TCheckCountFacet[] = [
  { key: 'open-checks', query: { isOpen: true } },
];

const HOME_MEMBER_FACETS: readonly TMemberCountFacet[] = [
  { key: 'all-clients', query: { role: 'client' } },
  { key: 'all-singles', query: { role: 'single' } },
];

const toCounterTotal = (counters: TCounters | undefined, bucket: TCounterBucket, keys: readonly string[]): number | undefined => {
  if (!counters) {
    return undefined;
  }

  return sumKeys(counters, bucket, keys);
};

const toOpenInterviewCount = (interviews: readonly TStaffInterview[] | undefined): number | undefined => {
  if (!interviews) {
    return undefined;
  }

  return interviews.filter(interview => OPEN_INTERVIEW_STATUSES.includes(interview.status)).length;
};

const toHomeCount = (source: THomeCountSource, sources: THomeSources): number | undefined => {
  const { counters } = sources;

  switch (source) {
    case 'own-applicants':
      return toCounterTotal(counters, OWN_STATUS, MY_APPLICANTS_OPEN_STATUSES);
    case 'pipeline-applicants':
      return toCounterTotal(counters, PIPELINE_STATUS, MY_APPLICANTS_OPEN_STATUSES);
    case 'final-approval':
      return toCounterTotal(counters, PIPELINE_STATUS, ['pending_final_approval']);
    case 'open-holds':
      return toCounterTotal(counters, REVIEW_OPEN, ['open']);
    case 'book-clients':
      return toCounterTotal(counters, BOOK_ROLE, ['client']);
    case 'book-singles':
      return toCounterTotal(counters, BOOK_ROLE, ['single']);
    case 'own-interviews':
    case 'all-interviews':
      return toOpenInterviewCount(sources.interviews);
    case 'open-checks':
      return sources.openChecks;
    case 'all-clients':
      return sources.allClients;
    case 'all-singles':
      return sources.allSingles;
    case 'unread-messages':
      return sources.unreadMessages;
    default:
      return undefined;
  }
};

const toHomeTiles = (specs: readonly THomeTileSpec[], sources: THomeSources): THomeTile[] => specs
  .map(spec => ({ id: spec.id, href: spec.href, count: toHomeCount(spec.source, sources) }));

const hasHomeSource = (specs: readonly THomeTileSpec[], source: THomeCountSource): boolean => specs
  .some(spec => spec.source === source);

export type {
  THomeSources,
};
export {
  HOME_CHECK_FACETS,
  HOME_MEMBER_FACETS,
  hasHomeSource,
  toHomeTiles,
};
